import { Field, useField } from "formik";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import { CheckPicker } from "rsuite";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import ReactSlider from "react-slider";
import jssVariables from "../../../jss_setup";
import { TranslationContext } from "../../../umbilical_tools";

// ============================== General styles ==============================
export const commonInputGroupStyles = {
  inputGroup: {
    display: "grid",
    width: "100%",
    color: jssVariables.colors.text.dark,
    alignContent: "space-between",
    fontFamily: jssVariables.fontFamily,
  },
  label: {
    textTransform: "capitalize",
    display: "inline-grid",
    gridTemplateColumns: "1fr auto",
    color: jssVariables.colors.text.dark,
    fontSize: "medium",
  },
  errorMessage: {
    color: jssVariables.colors.text.dark,
    justifySelf: "right",
  },
};

export const autoCompleteInputStyles = createUseStyles({
  inputGroup: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    ...(props?.customStyles?.inputGroup ?? {}),
  }),
  label: (props) => ({
    ...commonInputGroupStyles.label,
    ...(props?.customStyles?.label ?? {}),
  }),
  inputGroupBody: (props) => ({
    height: "3rem",
    ...(props?.customStyles?.inputGroupBody ?? {}),
  }),
  noLabelBodyContr: (props) => ({
    height: "3rem",
    ...(props?.customStyles?.noLabelBodyContr ?? {}),
  }),
  errorMessage: (props) => ({
    ...commonInputGroupStyles.errorMessage,
    ...(props?.customStyles?.errorMessage ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    inputGroup: (props) => ({
      width: "100%",
      ...(props?.customStyles?.inputGroup?.smallscreen ?? {}),
    }),
  },
});
// ============================================================================

export function renderError(errors) {
  return errors ? (
    <div className="InputGroup__error">{errors}</div>
  ) : (
    <div className="InputGroup__error" />
  );
}

const inputFieldStyles = createUseStyles({
  inputGroup: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    ..._.get(props, "customStyles.inputGroup", {}),
  }),
  label: (props) => ({
    ...commonInputGroupStyles.label,
    ..._.get(props, "customStyles.label", {}),
  }),
  requiredLabel: (props) => ({
    fontWeight: "normal",
    ..._.get(props, "customStyles.requiredLabel", {}),
  }),
  input: (props) => ({
    padding: `calc(${jssVariables.normal_gap} * 1.1) calc(${jssVariables.normal_gap} * 1)`,
    border: `1px solid ${jssVariables.colors.border}`,
    borderRadius: "5px",
    width: "100%",
    ..._.get(props, "customStyles.input", {}),
  }),
  errorMessage: (props) => ({
    ...commonInputGroupStyles.errorMessage,
    ..._.get(props, "customStyles.errorMessage", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    inputGroup: (props) => ({
      ..._.get(props, "customStyles.inputGroup.smallscreen", {}),
    }),
    input: (props) => ({
      padding: `calc(${jssVariables.normal_gap} * 1) calc(${jssVariables.normal_gap} * 1)`,
      width: "100%",
      ..._.get(props, "customStyles.input.smallscreen", {}),
    }),
  },
});
export const InputField = ({
  id,
  as = "input",
  type = "text",
  label,
  name,
  placeholder,
  disabled,
  customStyles,
  isRequired,
  rows,
  extraProps = {},
}) => {
  const classes = inputFieldStyles({ customStyles });

  const { t } = useContext(TranslationContext);

  if (type) extraProps["type"] = type;
  if (as === "textarea") extraProps["rows"] = rows;

  return (
    <div className={classes.inputGroup} id={id}>
      <label className={classes.label}>
        {label} {isRequired && <span className={classes.requiredLabel}>{t("required")}</span>}
      </label>
      <Field
        as={as}
        className={`${classes.input} bg-white`}
        placeholder={placeholder}
        name={name}
        rows={rows}
        disabled={disabled}
        {...extraProps}
      />
    </div>
  );
};
InputField.defaultProps = {
  as: "input",
  type: "text",
  label: "",
  placeholder: "",
  disabled: false,
  customStyles: {},
  isRequired: false,
  rows: 10,
  id: "",
};
InputField.propTypes = {
  as: PropTypes.oneOf(["input", "textarea"]),
  type: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  customStyles: PropTypes.object,
  name: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  rows: PropTypes.number,
  id: PropTypes.string,
};

export const TextAreaField = ({
  type,
  label,
  name,
  placeholder,
  disabled,
  customStyles,
  isRequired,
  id,
}) => {
  const classes = inputFieldStyles({ customStyles });

  const { t } = useContext(TranslationContext);

  const [meta] = useField({ label, name, placeholder, disabled });

  const extraProps = {};
  if (type) {
    extraProps["type"] = type;
  }

  return (
    <div className={classes.inputGroup} id={id}>
      <label className={classes.label}>
        {label} {isRequired && <span className={classes.requiredLabel}>{t("required")}</span>}
      </label>
      <Field
        as="textarea"
        className={`${classes.input} bg-white`}
        placeholder={placeholder}
        name={name}
        disabled={disabled}
        {...extraProps}
      />
      {!meta.touched && !meta.error ? (
        <small className={classes.errorMessage}>{meta.error}</small>
      ) : null}
    </div>
  );
};
TextAreaField.defaultProps = {
  label: "",
  placeholder: "",
  disabled: false,
  customStyles: {},
  isRequired: false,
  id: "",
};
TextAreaField.propTypes = {
  type: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  customStyles: PropTypes.object,
  name: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  id: PropTypes.string,
};

const checkPickerFieldStyles = createUseStyles({
  inputGroup: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    ..._.get(props, "customStyles.inputGroup", {}),
  }),
  label: (props) => ({
    ...commonInputGroupStyles.label,
    ..._.get(props, "customStyles.label", {}),
  }),
  errorMessage: (props) => ({
    ...commonInputGroupStyles.errorMessage,
    ..._.get(props, "customStyles.errorMessage", {}),
  }),
});
const CheckPickerComponent = ({
  field, // { name, value, onChange, onBlur }
  form: { touched, errors, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  ...props
}) => {
  return (
    <React.Fragment>
      <CheckPicker
        size="lg"
        placement="auto"
        searchable={props.searchable}
        value={field.value}
        data={[
          {
            value: "ACC",
            label: "Accommodation",
          },
          {
            value: "MI",
            label: "Tours/Experiences",
          },
        ]} //props.data}
        groupBy={props.groupBy}
        onChange={(e) => setFieldValue(field.name, e)}
      />
      {touched[field.name] && errors[field.name] && (
        <div className={props.styles.errorMessage}>{errors[field.name]}</div>
      )}
    </React.Fragment>
  );
};
CheckPickerComponent.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  searchable: PropTypes.bool,
  groupBy: PropTypes.string,
  styles: PropTypes.object,
};
export const CheckPickerField = ({ name, label, groupBy, data, searchable }) => {
  const classes = checkPickerFieldStyles();

  return (
    <div className={classes.inputGroup}>
      <label className={classes.label}>{label}</label>
      <Field
        name={name}
        styles={classes}
        data={data}
        searchable={searchable}
        groupBy={groupBy}
        component={CheckPickerComponent}
      />
    </div>
  );
};
CheckPickerField.defaultProps = {
  name: "",
  data: [],
  label: "",
  groupBy: "",
  searchable: true,
};
CheckPickerField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  data: PropTypes.array.isRequired,
  searchable: PropTypes.bool,
  groupBy: PropTypes.string,
};

const reactSliderFieldStyles = createUseStyles({
  inputGroup: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    ..._.get(props, "customStyles.inputGroup", {}),
  }),
  label: (props) => ({
    ...commonInputGroupStyles.label,
    display: "block",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.label", {}),
  }),
  subLabel: (props) => ({
    fontWeight: "normal",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.subLabel", {}),
  }),
  errorMessage: (props) => ({
    ...commonInputGroupStyles.errorMessage,
    ..._.get(props, "customStyles.errorMessage", {}),
  }),
  reactSlider: (props) => ({
    height: "2rem",
    ...(props.singleThumb
      ? {
          "& > div:first-child": { background: jssVariables.colors.base_color },
        }
      : {
          "& > div:nth-child(2)": {
            background: jssVariables.colors.base_color,
          },
        }),
    ..._.get(props, "customStyles.reactSlider", {}),
  }),
  thumb: (props) => ({
    color: "white",
    background: jssVariables.colors.base_color,
    cursor: "pointer",
    borderRadius: "50%",
    fontSize: 0,
    height: "1.5rem",
    width: "1.5rem",
    padding: `calc(${jssVariables.normal_gap} / 4) calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.thumb", {}),
  }),
  track: (props) => ({
    height: ".2rem",
    background: "lightgray",
    borderRadius: 0,
    marginTop: `calc(${jssVariables.normal_gap} / 1.1)`,
    ..._.get(props, "customStyles.track", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    inputGroup: (props) => ({
      ..._.get(props, "customStyles.inputGroup.smallscreen", {}),
    }),
  },
});
const ReactSliderFieldComponent = ({ field, form: { setFieldValue }, customStyles, ...props }) => {
  const { minMax } = props;
  const classes = reactSliderFieldStyles({
    customStyles,
    singleThumb: !field.value?.length,
  });

  return (
    <React.Fragment>
      <ReactSlider
        value={field.value}
        onChange={(value) => {
          setFieldValue(field.name, value);
        }}
        min={minMax[0]}
        max={minMax[1]}
        minDistance={1}
        className={classes.reactSlider}
        thumbClassName={classes.thumb}
        trackClassName={classes.track}
        renderThumb={(props, state) => <div {...props}>{state.valueNow}</div>}
      />
    </React.Fragment>
  );
};
ReactSliderFieldComponent.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  customStyles: PropTypes.object,
  minMax: PropTypes.array.isRequired,
};
export const ReactSliderField = ({ id, name, label, values, minMax, uom, customStyles }) => {
  const classes = reactSliderFieldStyles({ customStyles });

  return (
    <div className={classes.inputGroupSlider} id={id}>
      <label className={classes.label}>
        {label}
        {uom && <span className={classes.subLabel}>{` (${uom})`}</span>}
      </label>
      <Field
        name={name}
        values={values}
        minMax={minMax}
        customStyles={customStyles}
        component={ReactSliderFieldComponent}
      />
    </div>
  );
};
ReactSliderField.defaultProps = {
  name: "",
  label: "",
  values: [],
  minMax: [0, 100],
  uom: "",
  id: "",
};
ReactSliderField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  values: PropTypes.array.isRequired,
  minMax: PropTypes.array.isRequired,
  uom: PropTypes.string,
  customStyles: PropTypes.object,
  id: PropTypes.string,
};
